import { useState } from 'react';
import { UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ExistingUserPane } from './ExistingUserPane';
import { NewUserPane } from './NewUserPane';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

type UserPane = 'new' | 'existing';

const PANE_OPTIONS: { key: UserPane; label: string }[] = [
    { key: 'new', label: 'New user' },
    { key: 'existing', label: 'Existing user' }
];

interface AddPracticeUserModalProps {
    practiceId: string;
    practiceName: string;
    /** Emails already linked to this practice - passed through to ExistingUserPane. */
    existingEmails: Set<string>;
    onClose: () => void;
}

// Single "Invite user" entry point for the practice Users tab. A segmented
// toggle (same chrome as DirectionToggle/CommunicationsGranularityToggle) switches
// between the two panes; each pane owns its own form state, mutation, and
// footer, so this shell only owns which pane is showing. Defaults to "New
// user" and, since the parent unmounts this modal on close, the pane choice
// naturally resets to that default the next time it's opened.
export function AddPracticeUserModal({
    practiceId,
    practiceName,
    existingEmails,
    onClose
}: AddPracticeUserModalProps) {
    const [pane, setPane] = useState<UserPane>('new');
    // Each pane owns its own mutation, so the in-flight flag has to come back
    // up here: Escape, the X and the pane toggle all have to respect it.
    const [isBusy, setIsBusy] = useState(false);

    useEscapeKey(onClose, !isBusy);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-practice-user-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[440px] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                        <UserPlusIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="add-practice-user-modal-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Invite a user
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            Add someone new or link an existing account to{' '}
                            <span className="font-semibold text-gray-900">{practiceName}</span>.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isBusy}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-4">
                    <div
                        className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5 h-9"
                        role="group"
                        aria-label="User type"
                    >
                        {PANE_OPTIONS.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                aria-pressed={pane === option.key}
                                disabled={isBusy}
                                onClick={() => setPane(option.key)}
                                className={`flex-1 h-full rounded text-[13px] font-semibold transition-colors ${
                                    pane === option.key
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 font-medium'
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {pane === 'new' ? (
                    <NewUserPane
                        practiceId={practiceId}
                        practiceName={practiceName}
                        onClose={onClose}
                        onBusyChange={setIsBusy}
                    />
                ) : (
                    <ExistingUserPane
                        practiceId={practiceId}
                        practiceName={practiceName}
                        existingEmails={existingEmails}
                        onClose={onClose}
                        onBusyChange={setIsBusy}
                    />
                )}
            </div>
        </div>
    );
}
